<main class="container layout-39">
  <div id="containerLeftMenu" class="left-menu-container">
    <left-menu />
  </div>
  <section id="column2">
    <div class="row">
      <div class="col col-12">
        <h1>Entra Users Sync</h1>

        <!-- Global sync staleness threshold config -->
        @if (!loading) {
<div class="card mb-20">
          <div class="card-body">
            <h3>Global Sync Trigger</h3>
            <p class="muted no-top-margin">
              If the <strong>oldest</strong> user <code>LastRoleSyncAtUtc</code> is older than this threshold,
              the next authenticated request will trigger a global Entra→DB sync.
            </p>

            @if (configLoading) {
<app-users-sync-config-skeleton></app-users-sync-config-skeleton>
}

            @if (!configLoading && configSuccessMessage) {
<div class="alert alert-success">
              <mat-icon>check_circle</mat-icon>
              {{ configSuccessMessage }}
            </div>
}

            @if (!configLoading && configError) {
<div class="alert alert-danger">
              <mat-icon>error_outline</mat-icon>
              {{ configError }}
            </div>
}

            @if (!configLoading) {
<div class="row row-align-end">
              <div class="col-md-4 field-col">
                <label class="field-label">
                  Stale threshold (hours)
                </label>
                <input
                  class="form-control"
                  type="number"
                  min="1"
                  max="8760"
                  [(ngModel)]="staleThresholdHours" />
              </div>

              <div class="col-md-8 config-actions">
                <button
                  type="button"
                  class="btn btn-outline-secondary no-arrow"
                  (click)="resetConfig()"
                  [disabled]="configSaving || !hasConfigChanges">
                  Reset
                </button>
                <button
                  type="button"
                  class="btn btn-primary no-arrow"
                  (click)="saveConfig()"
                  [disabled]="configSaving || !hasConfigChanges">
                  @if (!configSaving) {
<span>Save</span>
}
                  @if (configSaving) {
<span>
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving...
                  </span>
}
                </button>
              </div>
            </div>
}
          </div>
        </div>
}
        
        @if (loading) {
<app-users-sync-skeleton></app-users-sync-skeleton>
}

        <!-- Success Message -->
        @if (successMessage) {
<div class="alert alert-success">
          <mat-icon>check_circle</mat-icon>
          {{ successMessage }}
        </div>
}

        <!-- Error Message -->
        @if (error) {
<div class="alert alert-danger">
          <mat-icon>error_outline</mat-icon>
          {{ error }}
        </div>
}

        <!-- Sync Status Card -->
        @if (!loading && status) {
<div class="card mb-20">
          <div class="card-body">
            <h3>Sync Status</h3>

            @if (jobStatus) {
<div class="alert alert-info job-status">
              <div class="job-status__header">
                <div>
                  <strong>Background job:</strong>
                  <span class="badge" [ngClass]="jobStatus.isQueuedOrRunning ? 'badge-primary' : 'badge-secondary'">
                    {{ jobStatus.isQueuedOrRunning ? 'Queued / Running' : 'Idle' }}
                  </span>
                </div>
                @if (jobStatus.queuedAtUtc) {
<div><strong>Queued:</strong> {{ formatDate(jobStatus.queuedAtUtc) }}</div>
}
                @if (jobStatus.dequeuedAtUtc) {
<div><strong>Dequeued:</strong> {{ formatDate(jobStatus.dequeuedAtUtc) }}</div>
}
                @if (jobStatus.completedAtUtc) {
<div><strong>Queue idle since:</strong> {{ formatDate(jobStatus.completedAtUtc) }}</div>
}
              </div>
              <div class="job-status__subtext">
                Global sync runs in the background (app-only) and does not block requests.
              </div>
            </div>
}
            
            <div class="row status-row">
              <div class="col-md-6">
                <p>
                  <strong>State:</strong> 
                  <span class="badge" [ngClass]="getStateClass()">
                    <mat-icon class="icon-16">
                      {{ getStateIcon() }}
                    </mat-icon>
                    {{ status.state }}
                  </span>
                </p>
                @if (status.currentActivity) {
<p><strong>Activity:</strong> {{ status.currentActivity }}</p>
}
                @if (status.startedAt) {
<p><strong>Started:</strong> {{ formatDate(status.startedAt) }}</p>
}
                @if (status.completedAt) {
<p><strong>Completed:</strong> {{ formatDate(status.completedAt) }}</p>
}
              </div>
              <div class="col-md-6 status-actions">
                <button 
                  type="button"
                  class="btn btn-primary no-arrow"
                  (click)="triggerSync()"
                  [disabled]="triggering || status.state === 'Running' || jobStatus?.isQueuedOrRunning">
                  @if (!triggering) {
<mat-icon>sync</mat-icon>
}
                  @if (!triggering) {
<span>Queue Entra Sync Job</span>
}
                  @if (triggering) {
<span>
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Starting...
                  </span>
}
                </button>
              </div>
            </div>

            <!-- Progress Bar -->
            @if (status.state === 'Running') {
<div class="progress-wrapper">
              <div class="progress-header">
                <span>Processing groups...</span>
                <span>{{ status.groupsProcessed }} / {{ status.groupsTotal }} ({{ getProgressPercentage() }}%)</span>
              </div>
              <mat-progress-bar 
                mode="determinate" 
                [value]="getProgressPercentage()">
              </mat-progress-bar>
            </div>
}
          </div>
        </div>
}

        <!-- Last Sync Result Summary -->
        @if (!loading && status?.lastResult) {
<div class="card mb-20">
          <div class="card-body">
            <h3>Last Sync Result</h3>
            
            <div class="row">
              <div class="col-md-3 col-sm-6 stat-col">
                <div class="stat-card stat-card--users">
                  <h4 class="stat-card__value stat-card__value--users">{{ status.lastResult.entraUsersDistinct }}</h4>
                  <p class="stat-card__label">Users in Groups</p>
                </div>
              </div>
              
              <div class="col-md-3 col-sm-6 stat-col">
                <div class="stat-card stat-card--inserted">
                  <h4 class="stat-card__value stat-card__value--inserted">{{ status.lastResult.inserted }}</h4>
                  <p class="stat-card__label">Inserted</p>
                </div>
              </div>
              
              <div class="col-md-3 col-sm-6 stat-col">
                <div class="stat-card stat-card--updated">
                  <h4 class="stat-card__value stat-card__value--updated">{{ status.lastResult.updated }}</h4>
                  <p class="stat-card__label">Updated</p>
                </div>
              </div>
              
              <div class="col-md-3 col-sm-6 stat-col">
                <div class="stat-card stat-card--deactivated">
                  <h4 class="stat-card__value stat-card__value--deactivated">{{ status.lastResult.deactivated }}</h4>
                  <p class="stat-card__label">Deactivated</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4 col-sm-6">
                <p><strong>Reactivated:</strong> {{ status.lastResult.reactivated }}</p>
              </div>
              <div class="col-md-4 col-sm-6">
                <p><strong>Unchanged:</strong> {{ status.lastResult.unchanged }}</p>
              </div>
              <div class="col-md-4 col-sm-6">
                <p><strong>Duration:</strong> {{ formatDuration(status.lastResult.duration) }}</p>
              </div>
            </div>

            @if (status.lastResult.errors && status.lastResult.errors.length > 0) {
<div class="alert alert-warning mt-15">
              <strong>Errors during sync ({{ status.lastResult.errors.length }}):</strong>
              <ul class="errors-list">
                @for (error of status.lastResult.errors; track error) {
<li>{{ error }}</li>
}
              </ul>
            </div>
}

            @if (!status.lastResult.success && status.lastResult.errorMessage) {
<div class="alert alert-danger mt-15">
              <strong>Sync Failed:</strong> {{ status.lastResult.errorMessage }}
            </div>
}
          </div>
        </div>
}

        <!-- Temporary: CMID Graph test panel -->
        @if (!loading) {
<div class="card debug-card mb-20">
          <div class="card-body">
            <h3 class="debug-header">
              <mat-icon>bug_report</mat-icon>
              CMID Graph Test (temporary)
            </h3>

            <p class="muted no-top-margin">
              Calls <code>/api/v1/admin/users/entra-sync/test-graph</code> and returns sample members.
            </p>

            <div class="row mb-10">
              <div class="col-md-6 field-col">
                <label class="field-label">Group (display name or object id)</label>
                <input class="form-control" [(ngModel)]="debugGroupName" placeholder="e.g. AIModernization-Admins" />
              </div>

              <div class="col-md-2 field-col">
                <label class="field-label">Delegated</label>
                <select class="form-control" [(ngModel)]="debugDelegated">
                  <option [ngValue]="true">true (OBO)</option>
                  <option [ngValue]="false">false (app-only)</option>
                </select>
              </div>

              <div class="col-md-2 field-col">
                <label class="field-label">First page</label>
                <select class="form-control" [(ngModel)]="debugFirstPageOnly">
                  <option [ngValue]="true">true</option>
                  <option [ngValue]="false">false</option>
                </select>
              </div>

              <div class="col-md-2 field-col">
                <label class="field-label">Top results</label>
                <input class="form-control" type="number" [(ngModel)]="debugTopResults" min="1" max="999" />
              </div>
            </div>

            <div class="actions-right">
              <button type="button" class="btn btn-outline-primary no-arrow" (click)="testGraph()" [disabled]="debugTesting">
                @if (!debugTesting) {
<mat-icon>play_circle</mat-icon>
}
                @if (!debugTesting) {
<span>Test Graph</span>
}
                @if (debugTesting) {
<span>
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Testing...
                </span>
}
              </button>
            </div>

            @if (debugError) {
<div class="alert alert-danger mt-15">
              <mat-icon>error_outline</mat-icon>
              {{ debugError }}
            </div>
}

            @if (debugTestResult) {
<div class="alert alert-info mt-15 debug-result">
              <div class="debug-result__meta">
                <strong>MembersReturned:</strong> {{ debugTestResult.membersReturned }}
                &nbsp;|&nbsp;
                <strong>Delegated:</strong> {{ debugTestResult.delegated }}
              </div>
              <pre class="debug-result__json">{{ debugTestResult | json }}</pre>
            </div>
}
          </div>
        </div>
}

        <!-- Info when no last result -->
        @if (!loading && status && !status.lastResult && status.state === 'Idle') {
<div class="card">
          <div class="card-body empty-state">
            <mat-icon class="empty-state__icon">people_outline</mat-icon>
            <p class="empty-state__title">No sync has been performed yet</p>
            <p class="empty-state__subtitle">Click "Sync Users from Entra" to start the first synchronization</p>
          </div>
        </div>
}
      </div>
    </div>
  </section>
</main>
